import {
  selectDriversHistoryList,
  selectDriversHistoryListStatus,
} from "../../fleet/fleetSlice";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  uppercaseFirstLetter,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterHourMinute,
  TextCapitalized,
  Spacer,
  Avatar,
  RemoveRedEyeOutlined,
  ButtonDS,
  DownloadOutlined,
  SidePanelDS,
} from "@qivia/ui";
import { useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

export const DriverHistorySection = () => {
  const pageName = "vehicles";
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const allDriversHistoryList = useAppSelector(selectDriversHistoryList);
  const allDriversHistoryListStatus = useAppSelector(
    selectDriversHistoryListStatus,
  );
  const driversHistoryList = useMemo(
    () => allDriversHistoryList.filter((dh) => dh.vehicleUuid === params.id),
    [allDriversHistoryList, params.id],
  );

  const lastDriver = useMemo(() => driversHistoryList[0], [driversHistoryList]);

  const driversHistoryExcelColumns = useMemo(
    () => [
      "analyticCode1",
      "lastName",
      "firstName",
      "startOfUsage",
      "startOfUsageTime",
      "endOfUsage",
      "endOfUsageTime",
    ],
    [],
  );

  const handleClickDriver = useCallback(() => {
    const firstPath = location.pathname.split("/")[1]; // home or departments
    navigate(`/${firstPath}/drivers/${lastDriver?.driverUuid}`);
  }, [lastDriver?.driverUuid, navigate]);

  const handleDownloadDriverHistory = useCallback(() => {
    if (driversHistoryList.length <= 0) return;
    const headerTitles = driversHistoryExcelColumns.map((key) =>
      uppercaseFirstLetter(t(`driversHistory.column.${key}`)),
    );

    const bodyList = driversHistoryList.map((dh) => [
      dh.driverInfo.analyticCode1 ?? "",
      dh.driverInfo.lastName,
      dh.driverInfo.firstName,
      dateFormatterDayMonthYearLong2Digits(new Date(dh.startOfUsage)),
      dateFormatterHourMinute(new Date(dh.startOfUsage)),
      dh.endOfUsage
        ? dateFormatterDayMonthYearLong2Digits(new Date(dh.endOfUsage))
        : "",
      dh.endOfUsage ? dateFormatterHourMinute(new Date(dh.endOfUsage)) : "",
    ]);

    const file = createFileXlsx({
      tabTitle: t(`${pageName}.view.title`),
      headerTitles,
      body: bodyList,
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName: "QIVIA_" + t("vehicles.driversHistory.excel.title") + ".xlsx",
      }),
    );
  }, [dispatch, driversHistoryExcelColumns, driversHistoryList, t]);

  //TODO WAITING FOR DEPARTMENT ROUTE
  if (allDriversHistoryListStatus === "idle") {
    return;
  }

  return (
    <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
      <SectionTitle>
        <TextCapitalized>
          {t("vehicles.sidepanel.driversHistory")}
        </TextCapitalized>
      </SectionTitle>
      {lastDriver && (
        <>
          <Spacer y={2} />
          <SidePanelDS.ActionBlock>
            <SidePanelDS.SectionedActionStripe
              left={
                <StyledRowCenter>
                  <TextCapitalized>
                    <Avatar
                      size={"S"}
                      color={"orange"}
                      lastName={lastDriver.driverInfo.lastName}
                      firstName={lastDriver.driverInfo.firstName}
                    />
                  </TextCapitalized>
                  <Spacer x={0.5} />
                  {lastDriver.driverInfo.firstName.concat(
                    " ",
                    lastDriver.driverInfo.lastName,
                  )}
                </StyledRowCenter>
              }
              right={
                <StyledLittleIcon>
                  <RemoveRedEyeOutlined
                    key="driver"
                    onClick={handleClickDriver}
                  />
                  <Spacer x={1} />
                </StyledLittleIcon>
              }
            />
          </SidePanelDS.ActionBlock>
          <Spacer y={1} />
          <ButtonDS
            sizeButton="S"
            format="hug"
            buttonType="secondary"
            text={t("vehicles.sidepanel.downloadDriversHistoryList")}
            leftIcon={<DownloadOutlined />}
            onClick={handleDownloadDriverHistory}
          />
        </>
      )}
    </SidePanelDS.Section>
  );
};

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;
